import { DateOnly, format } from '@if/core/date'
import { formatCurrency } from '@if/core/utils'
import { ActionLink, Caption, FieldValue, Icon, StrongValue } from '@if/mui'
import {
  Box,
  Card,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material'
import type { PendingLenderPaymentDto } from '@src/api/lender-payment/pending-lender-payments-api'
import { useCompany } from '@src/services/company-hooks'
import { useManageMerchantsBaseUrl } from '@src/services/manage-merchants-hook'
import { EDisbursementTarget } from '@src/types/constants'
import { ChevronDown, ChevronRight, CircleAlert } from 'lucide-react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  blockReasonOf,
  DAILY_DISBURSEMENT_WARNING_THRESHOLD,
  exceedsDailyThreshold,
  hasNegativeSelectedTotal,
  selectionStateOf,
  signedAmountOf,
  type BlockReason,
  type PayeeGroup,
  type PayeeTypeGroup,
} from './pending-lender-payment-grouping'

// How each blocking reason reads: a payment that is not due yet is an error tint, one waiting on its
// closing is sky — different in kind, not a worse version of the same thing.
const BLOCK_TINT: Record<BlockReason, 'error' | 'sky'> = {
  future: 'error',
  closingNotFinalized: 'sky',
}
// `satisfies` rather than a type annotation: i18next keys are typed literals, and annotating the
// record would widen them to string and break the lookup.
const BLOCK_MESSAGE = {
  future: 'disburse.selection.futureBlocked',
  closingNotFinalized: 'disburse.selection.closingNotFinalizedBlocked',
} as const satisfies Record<BlockReason, string>

// The `xs` step of the design system's icon scale. The slot is held open whether or not a row
// carries a notice, so every name starts on the same column.
const NOTICE_SLOT_WIDTH = 14

interface Props {
  group: PayeeTypeGroup
  selected: ReadonlySet<number>
  today: DateOnly
  onToggleAll: (payments: PendingLenderPaymentDto[], select: boolean) => void
  onToggleOne: (operationId: number) => void
}

// One card per payee type. Its header band carries the tri-state "select all" of the card; each
// payee inside gets a header row with its own tri-state box that drives the payee's payments.
// Every "select all" acts on the group's selectable payments only — a future-dated payment is
// never ticked by one.
const PayeeTypeCard = ({ group, selected, today, onToggleAll, onToggleOne }: Props) => {
  const { t } = useTranslation()
  const state = selectionStateOf(group.selectable, selected)
  const manageMerchantsBaseUrl = useManageMerchantsBaseUrl()

  return (
    <Card>
      <Table size="small">
        <TableHead>
          <TableRow
            sx={(theme) => ({
              backgroundColor: theme.vars.palette.tableHead.bg,
              '& .MuiTableCell-root, & .MuiCheckbox-root, & .MuiTypography-root': {
                color: theme.vars.palette.tableHead.fg,
              },
            })}
          >
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={state === 'all'}
                indeterminate={state === 'some'}
                disabled={group.selectable.length === 0}
                onChange={() => onToggleAll(group.selectable, state === 'none')}
                slotProps={{ input: { 'aria-label': t('disburse.selection.selectAllOfType') } }}
              />
            </TableCell>
            <TableCell>
              <Stack direction="row" spacing={1} alignItems="center">
                <StrongValue>{t(`enum.targetType.${group.payeeType}`)}</StrongValue>
                <Chip size="small" label={t('disburse.selection.paymentCount', { count: group.payments.length })} />
              </Stack>
            </TableCell>
            <TableCell>{t('disburse.selection.description')}</TableCell>
            <TableCell>{t('disburse.selection.paymentOn')}</TableCell>
            <TableCell align="right">
              <StrongValue>{formatCurrency(group.total)}</StrongValue>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {group.payees.map((payee) => (
            <PayeeRows
              key={payee.payeeId}
              payee={payee}
              // manage-merchants keys its page on the Merchants API id, which is the payee id as is;
              merchantPageUrl={
                group.payeeType === EDisbursementTarget.Merchant
                  ? `${manageMerchantsBaseUrl}/merchant/${encodeURIComponent(payee.payeeId)}/view`
                  : null
              }
              selected={selected}
              today={today}
              onToggleAll={onToggleAll}
              onToggleOne={onToggleOne}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  )
}

interface PayeeRowsProps {
  payee: PayeeGroup
  /** The payee's page in manage-merchants, or null when it has none: its name then stays plain text. */
  merchantPageUrl: string | null
  selected: ReadonlySet<number>
  today: DateOnly
  onToggleAll: (payments: PendingLenderPaymentDto[], select: boolean) => void
  onToggleOne: (operationId: number) => void
}

// A payee's header row (collapsible, tri-state box, count chip, subtotal) followed by one row
// per payment. Rendered as a fragment so the rows stay in the card's single table; MUI's
// Collapse cannot wrap table rows, so the rows are conditionally rendered instead.
const PayeeRows = ({ payee, merchantPageUrl, selected, today, onToggleAll, onToggleOne }: PayeeRowsProps) => {
  const { t } = useTranslation()
  const company = useCompany()
  const [open, setOpen] = useState(true)
  const state = selectionStateOf(payee.selectable, selected)
  const selectedCount = payee.selectable.filter((p) => selected.has(p.operationId)).length
  // Mirrors the Monolith's group row: a payee holding an account-to-account whose closing is still
  // open is tinted too, so the wait is visible without expanding the payee.
  const holdsPendingClosing = payee.payments.some((p) => p.isPendingClosingFinalization)
  // What the payee itself is wrong about: a selection that would send money backwards, and a day
  // whose outgoing total is large enough to be worth a second look before submitting.
  const negativeTotal = hasNegativeSelectedTotal(payee, selected)
  const overDailyThreshold = exceedsDailyThreshold(payee)

  return (
    <Fragment>
      <TableRow
        sx={(theme) => ({
          backgroundColor: holdsPendingClosing
            ? theme.vars.palette.sky.backgroundHover
            : theme.vars.palette.action.hover,
        })}
      >
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={state === 'all'}
            indeterminate={state === 'some'}
            disabled={payee.selectable.length === 0}
            onChange={() => onToggleAll(payee.selectable, state === 'none')}
            slotProps={{ input: { 'aria-label': t('disburse.selection.selectAllOfPayee', { payee: payee.label }) } }}
          />
        </TableCell>
        <TableCell colSpan={3}>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? t('disburse.selection.collapse') : t('disburse.selection.expand')}
            >
              <Icon icon={open ? ChevronDown : ChevronRight} size="xs" />
            </IconButton>
            {merchantPageUrl ? (
              <ActionLink href={merchantPageUrl} target="_blank">
                {payee.label}
              </ActionLink>
            ) : (
              <StrongValue>{payee.label}</StrongValue>
            )}
            <Chip
              size="small"
              color={state === 'none' ? 'default' : 'primary'}
              label={
                state === 'none'
                  ? t('disburse.selection.paymentCount', { count: payee.payments.length })
                  : `${selectedCount} / ${payee.selectable.length}`
              }
            />
          </Stack>
        </TableCell>
        <TableCell align="right">
          <StrongValue>{formatCurrency(payee.total)}</StrongValue>
        </TableCell>
      </TableRow>
      {/* Messages about the payee as a whole, right under its header so they read as its own. */}
      {(negativeTotal || overDailyThreshold) && (
        <TableRow>
          <TableCell />
          <TableCell colSpan={4} sx={{ pt: 0, borderBottom: 'none' }}>
            <Stack spacing={0.5}>
              {negativeTotal && (
                <Caption sx={(theme) => ({ color: theme.vars.palette.error.soft })}>
                  {t('disburse.selection.negativeTotal')}
                </Caption>
              )}
              {overDailyThreshold && (
                <Caption sx={(theme) => ({ color: theme.vars.palette.warning.soft })}>
                  {t('disburse.selection.dailyThresholdWarning', {
                    threshold: formatCurrency(DAILY_DISBURSEMENT_WARNING_THRESHOLD),
                  })}
                </Caption>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      )}
      {open &&
        payee.payments.map((payment) => {
          const isSelected = selected.has(payment.operationId)
          const blocked = blockReasonOf(payment, today)

          const blockMessage =
            blocked === null
              ? null
              : t(BLOCK_MESSAGE[blocked], { deliveryOn: format(new DateOnly(payment.paymentOn), 'P') })

          const notice: string | null = blockMessage

          return (
            <TableRow
              key={payment.operationId}
              hover={blocked === null}
              selected={isSelected}
              onClick={blocked ? undefined : () => onToggleOne(payment.operationId)}
              sx={(theme) => ({
                cursor: blocked ? 'default' : 'pointer',
                // A blocked payment reads as blocked, not as a row to click. The stronger of the two
                // soft tints: the row never hovers, so the resting state is free to take it, and the
                // plain `background` wash is too faint to read across a full table row.
                ...(blocked && { backgroundColor: theme.vars.palette[BLOCK_TINT[blocked]].backgroundHover }),
              })}
            >
              <TableCell padding="checkbox">
                {blocked ? (
                  // A disabled control emits no pointer events, so the tooltip needs a wrapper to
                  // hang the hover off.
                  <Tooltip title={blockMessage}>
                    <span>
                      <Checkbox size="small" checked={false} disabled />
                    </span>
                  </Tooltip>
                ) : (
                  <Checkbox
                    size="small"
                    checked={isSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleOne(payment.operationId)}
                  />
                )}
              </TableCell>
              {/* The name opens the loan's calendar in a new tab; stop the click so it does not toggle the row. */}
              <TableCell sx={{ pl: 6 }} onClick={(e) => e.stopPropagation()}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Box component="span" sx={{ display: 'inline-flex', width: NOTICE_SLOT_WIDTH, flexShrink: 0 }}>
                    {blocked && notice && (
                      // The span carries the tooltip's ref, which the icon itself does not.
                      <Tooltip title={notice}>
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <Icon icon={CircleAlert} size="xs" color={BLOCK_TINT[blocked]} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  <ActionLink href={`/${company}/loans/${payment.loanId}`} target="_blank">
                    {payment.borrowerFullName}
                  </ActionLink>
                </Stack>
              </TableCell>
              {/* The Monolith's own sentence, verbatim — nothing is composed or reworded here. */}
              <TableCell>
                <Caption>{payment.description}</Caption>
              </TableCell>
              {/* Only a future date is itself the reason, so only that case recolors the date with
                  the error foreground the design system pairs with the tint. */}
              <TableCell sx={blocked === 'future' ? (theme) => ({ color: theme.vars.palette.error.soft }) : undefined}>
                <FieldValue color="inherit">{format(new DateOnly(payment.paymentOn), 'PP')}</FieldValue>
              </TableCell>
              <TableCell align="right">
                <FieldValue>{formatCurrency(signedAmountOf(payment))}</FieldValue>
              </TableCell>
            </TableRow>
          )
        })}
    </Fragment>
  )
}

export default PayeeTypeCard
